/* AXON motion layer (GSAP 3.13, self-hosted). Transform and opacity only, everything inside a
   prefers-reduced-motion: no-preference branch, nothing hidden before this file runs.
   GSAP reads the same easing curves and durations as the CSS (web-design-studio token rules). */
(() => {
  if (typeof window.gsap === "undefined") return;
  const css = getComputedStyle(document.documentElement);
  const ms = (n) => parseFloat(css.getPropertyValue(n)) || 0;
  const $ = (s, el = document) => el.querySelector(s);
  const $$ = (s, el = document) => [...el.querySelectorAll(s)];
  gsap.registerPlugin(ScrollTrigger, CustomEase);
  ["ease-out", "ease-expo", "ease-in-out", "ease-soft", "ease-exit"].forEach((n) => {
    const m = css.getPropertyValue(`--${n}`).match(/cubic-bezier\(([^)]+)\)/);
    if (m) CustomEase.create(n, m[1]);
  });
  gsap.defaults({ duration: ms("--t-reveal") / 1000, ease: "ease-expo" });
  const stagger = (n) => ms(n) / 1000;
  const once = (trigger, start = "top 82%") => ({ trigger, start, once: true });
  const inEditor = window.Shopify && window.Shopify.designMode;

  const mm = gsap.matchMedia();
  mm.add({ desktop: "(min-width: 960px)", motion: "(prefers-reduced-motion: no-preference)" }, (ctx) => {
    const { desktop, motion } = ctx.conditions;
    if (!motion) return;

    /* Signature move: the halves of the sentence hold while the day passes through the frame. */
    const strip = $("[data-reel]");
    if (strip && desktop) {
      const n = Number(strip.dataset.frames) || strip.children.length;
      const frame = strip.firstElementChild;
      const gap = parseFloat(getComputedStyle(strip).rowGap) || 24;
      if (n >= 3) {
        gsap.to(strip, {
          y: () => -(n - 2) * (frame.offsetHeight + gap),
          ease: "none",
          scrollTrigger: { trigger: ".hero__track", start: "top top", end: "bottom bottom", scrub: 0.6, invalidateOnRefresh: true },
        });
      }
    }
    if ($("[data-hero]") && !inEditor) {
      gsap.timeline({ defaults: { ease: "ease-expo" } })
        .from(".hero__half--a", { x: -48, autoAlpha: 0, duration: 0.7 })
        .from(".hero__half--b", { x: 48, autoAlpha: 0, duration: 0.7 }, "<0.09")
        .from(".reel", { autoAlpha: 0, duration: 0.8, ease: "ease-soft" }, "<")
        .from([".hero__foot", ".hero__actions"], { y: 24, autoAlpha: 0, duration: 0.6, stagger: 0.08 }, "<0.2");
    }

    /* The latest and every product grid: heading, then the cards in reading order. */
    $$(".latest").forEach((sec) => {
      gsap.from($$(".latest__head > *", sec), { y: 28, autoAlpha: 0, stagger: stagger("--stagger-line"), scrollTrigger: once(sec) });
    });
    const gridIn = (grid) => {
      if (grid._moved) return;
      grid._moved = true;
      ScrollTrigger.batch($$(":scope > li", grid), {
        start: "top 92%",
        once: true,
        onEnter: (els) => gsap.from(els, { y: 24, autoAlpha: 0, duration: 0.7, stagger: stagger("--stagger-item"), overwrite: true }),
      });
    };
    $$("[data-stagger]").forEach(gridIn);
    document.addEventListener("axon:content", (e) => { $$("[data-stagger]", e.detail).forEach(gridIn); ScrollTrigger.refresh(); });

    /* Drops: each field's pieces rise, then its name. */
    $$(".drop").forEach((drop) => {
      gsap.from($$(".drop__media > *", drop), { y: 32, autoAlpha: 0, stagger: stagger("--stagger-item"), scrollTrigger: once(drop, "top 75%") });
      gsap.from($$(".drop__text > *", drop), { y: 24, autoAlpha: 0, stagger: stagger("--stagger-line"), delay: 0.15, scrollTrigger: once(drop, "top 75%") });
    });

    /* Shop by collection: the heading, then each row slides up in order. */
    $$(".index").forEach((idx) => {
      gsap.from($$(".index__head > *", idx), { y: 28, autoAlpha: 0, stagger: stagger("--stagger-line"), scrollTrigger: once(idx) });
      gsap.from($$(".flow__row", idx), { y: 32, autoAlpha: 0, duration: 0.7, stagger: stagger("--stagger-item"), scrollTrigger: once($(".flow", idx), "top 88%") });
    });

    /* The day: each word rises as its panel arrives; the piece drifts against the scroll. */
    $$(".verb").forEach((panel) => {
      gsap.from($(".verb__word", panel), { yPercent: 24, autoAlpha: 0, duration: 1, scrollTrigger: once(panel, "top 55%") });
      gsap.from($$(".verb__text > *", panel), { y: 24, autoAlpha: 0, duration: 0.8, stagger: stagger("--stagger-line"), scrollTrigger: once(panel, "top 60%") });
      const shot = $(".verb__shot", panel);
      if (shot) {
        gsap.from(shot, { scale: 0.94, autoAlpha: 0, duration: 1.1, ease: "ease-soft", scrollTrigger: once(panel, "top 70%") });
        if (desktop) gsap.fromTo($(".verb__product, .photo", shot) || shot, { yPercent: 6 }, { yPercent: -6, ease: "none", scrollTrigger: { trigger: panel, start: "top bottom", end: "bottom top", scrub: true } });
      }
    });

    /* The kit: the statement rises line by line, then the picker, then the rows one by one. */
    $$(".kit").forEach((kit) => {
      gsap.from($$(".kit__title span", kit), { y: 40, autoAlpha: 0, stagger: stagger("--stagger-line"), scrollTrigger: once(kit) });
      const pk = $(".picker", kit);
      if (pk) gsap.from(pk, { y: 24, autoAlpha: 0, delay: 0.2, scrollTrigger: once(kit) });
      gsap.from($(".kit__foot", kit), { y: 24, autoAlpha: 0, delay: 0.3, scrollTrigger: once(kit) });
      const rows = $$(".spec", kit);
      if (rows.length) gsap.from(rows, { y: 28, autoAlpha: 0, duration: 0.7, stagger: stagger("--stagger-item"), scrollTrigger: once($(".kit__rows", kit)) });
    });

    /* The collection spotlight: the frame settles from a slight scale, the rows arrive in order. */
    $$(".shop").forEach((shop) => {
      // Phones pin the frame over the list, so it must stay opaque: it only fades in on wide screens.
      if (desktop) gsap.from($(".shop__frame", shop), { scale: 1.04, autoAlpha: 0, duration: 1.1, ease: "ease-soft", scrollTrigger: once(shop) });
      gsap.from($(".shop__title", shop), { y: 32, autoAlpha: 0, scrollTrigger: once(shop) });
      gsap.from($$(".item", shop), { y: 20, autoAlpha: 0, duration: 0.6, stagger: stagger("--stagger-item"), delay: 0.1, scrollTrigger: once($(".items", shop)) });
    });

    /* The gym: the tall picture opens from a mask, the column arrives in reading order. */
    $$(".gym").forEach((gym) => {
      gsap.from($(".gym__tall", gym), { clipPath: "inset(100% 0 0 0)", duration: ms("--t-open") / 1000, ease: "ease-in-out", scrollTrigger: once(gym, "top 70%") });
      gsap.from($(".gym__square", gym), { y: 40, autoAlpha: 0, delay: 0.5, scrollTrigger: once(gym, "top 70%") });
      gsap.from([$(".gym__title", gym), ...$$(".facts li", gym)], { y: 24, autoAlpha: 0, stagger: stagger("--stagger-line"), scrollTrigger: once($(".gym__text", gym)) });
      gsap.from($$(".t-acc", gym), { y: 16, autoAlpha: 0, duration: 0.6, stagger: stagger("--stagger-item"), scrollTrigger: once($(".faq", gym)) });
    });

    /* Collection and page heads: the name lands, then what it holds. */
    $$(".coll-head, .page-head").forEach((head) => {
      gsap.from($$(":scope > *, .coll-head__aside > *", head).filter((el) => !el.classList.contains("coll-head__aside")), { y: 28, autoAlpha: 0, stagger: stagger("--stagger-line") });
    });

    /* Product: the first shot settles, the information arrives in reading order. */
    $$(".pdp").forEach((pdp) => {
      const first = $(".pdp__shot", pdp);
      if (first) gsap.from(first, { scale: 1.03, autoAlpha: 0, duration: 1, ease: "ease-soft" });
      gsap.from($$(".pdp__info > *", pdp), { y: 20, autoAlpha: 0, duration: 0.6, stagger: stagger("--stagger-item"), delay: 0.1 });
      if (desktop) gsap.from($$(".pdp__shot", pdp).slice(1), { y: 32, autoAlpha: 0, stagger: stagger("--stagger-item"), scrollTrigger: once($$(".pdp__shot", pdp)[1] || pdp, "top 90%") });
    });

    /* Footer: the name returns. */
    const mark = $(".foot__mark");
    // No fade: the name is the last thing on every page and should never read as missing.
    if (mark) gsap.from(mark, { yPercent: 12, duration: 1.1, scrollTrigger: once(".foot", "top bottom") });
  });

  document.fonts && document.fonts.ready.then(() => ScrollTrigger.refresh());
  window.addEventListener("load", () => ScrollTrigger.refresh());
  document.addEventListener("shopify:section:load", () => ScrollTrigger.refresh());
})();
